import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/translations';
import { COMPANY_ADDRESS_LINES } from '../data/company';

const Footer: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <footer className="bg-brand-navy text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="bg-brand-blue p-2 rounded-lg">
                <Plane className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold">Delay24</span>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed max-w-md">
              {t.footer.description}
            </p>
            <div className="mt-6 space-y-2">
              <div className="flex items-center space-x-2 text-sm text-gray-300">
                <Mail className="h-4 w-4 text-brand-blue" />
                <span>info@delay24.com</span>
              </div>
              <div className="flex items-start space-x-2 text-sm text-gray-300">
                <MapPin className="h-4 w-4 text-brand-blue flex-shrink-0 mt-0.5" />
                <address className="not-italic leading-relaxed">
                  {COMPANY_ADDRESS_LINES.map((line) => (
                    <span key={line} className="block">{line}</span>
                  ))}
                </address>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-4">{t.footer.menu}</h3>
            <ul className="space-y-2">
              {[
                { to: '/', label: t.nav.home },
                { to: '/how-it-works', label: t.nav.howItWorks },
                { to: '/faq', label: t.nav.faq },
                { to: '/about', label: t.nav.about },
                { to: '/contact', label: t.nav.contact },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="text-gray-300 hover:text-brand-blue text-sm transition-colors duration-200">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-4">{t.footer.legal}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/terms" className="text-gray-300 hover:text-brand-blue text-sm transition-colors duration-200">
                  {t.footer.terms}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-gray-300 hover:text-brand-blue text-sm transition-colors duration-200">
                  {t.footer.privacy}
                </Link>
              </li>
              <li>
                <Link to="/price-list" className="text-gray-300 hover:text-brand-blue text-sm transition-colors duration-200">
                  {t.footer.priceList}
                </Link>
              </li>
              {language === 'tr' && (
                <li>
                  <Link to="/kvkk" className="text-gray-300 hover:text-brand-blue text-sm transition-colors duration-200">
                    {t.footer.kvkk}
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-700">
          <p className="text-center text-sm text-gray-400">{t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
